<template>
	<Fragment>
		<GenericForm
			ref="genericForm"
			:service="service"
			:id="id"
			:novo="novo"
			:initial-expression="criarObjeto"
			@save="salvarEvento"
		>
			<template v-slot:title>
				{{ novo ? 'Registrar' : 'Editar' }} Evento
			</template>
			<template #content="{ data: evento }">
				<v-card-text>
					<v-row>
						<v-col auto>
							<v-text-field
								label="Data"
								v-model="evento.data"
								type="date"
								:rules="[ obrigatorio ]"
							/>
						</v-col>
						<v-col auto>
							<v-text-field
								label="Hora"
								v-model="evento.horario"
								step="1800"
								min="00:00"
								max="23:30"
								type="time"
							/>
						</v-col>
					</v-row>
					<v-combobox
						label="Tipo do Evento / Sessão"
						v-model="evento.tipoDaSessao"
						:items="opcoes"
						item-text="nome"
						item-value="nome"
						return-object
						@change="(tipoDaSessao) => alterarTipoDaSessao(evento, tipoDaSessao)"
						:rules="[ obrigatorio ]"
					/>
					<template v-if="evento.entidade">
						<v-text-field
							v-if="evento.tipoDaSessao.tipoDaEntidade !== 'loja'"
							:label="displayEntidade[evento.tipoDaSessao.tipoDaEntidade]"
							v-model="evento.entidade.nome"
							disabled	
							readonly
							:rules="[ obrigatorio ]"
						/>
						<v-autocomplete
							v-else
							:label="$attrs.label || 'Loja'"
							:items="lojas"
							:value="evento.entidade"
							@change="valor => alterarLojaDoEvento(evento, valor)"
							item-text="nome"
							return-object
						/>
						<v-combobox
							label="Grau"
							v-if="evento.tipoDaSessao.requerGrau"
							v-model="evento.grauDaSessao"
							:items="grausDeSessao"
							:rules="[ obrigatorio ]"
						/>
						<v-text-field
							label="Certificação da Sessão"
							v-if="evento"
							v-model="evento.certificacaoDaSessao"
							clearable
						>
							<template #append-outer>
								<v-tooltip bottom max-width="240">
									<template v-slot:activator="{ on }">
										<v-icon v-on="on">
											mdi-help-circle-outline
										</v-icon>
									</template>
									Aparecerá no certificado ao invés do Tipo do Evento / Sessão
								</v-tooltip>
							</template>
						</v-text-field>
					</template>
	
					<v-textarea
						label="Descrição"
						v-model="evento.texto"
					/>
	
				</v-card-text>
	
				<v-subheader>Links</v-subheader>
				<v-list dense v-if="evento.links.length">
					<v-list-item v-for="(link, index) in evento.links" :key="index">
	
						<v-list-item-content>
							<a :href="link.url" target="_blank" rel="noopener noreferrer">{{ link.texto }}</a>
						</v-list-item-content>
	
						<v-list-item-action>
							<v-btn icon color="primary" @click="editarLink(index)">
								<v-icon>mdi-pencil</v-icon>
							</v-btn>

						</v-list-item-action>
						<v-list-item-action>
								
	
							<v-btn icon color="error" @click="removerLink(index)">
								<v-icon>mdi-delete</v-icon>
							</v-btn>
						</v-list-item-action>
					</v-list-item>
				</v-list>
	
				<v-list v-else-if="!evento.links.length">
					<v-list-item>
						<v-list-item-content class="font-italic">Nenhum link adicionado</v-list-item-content>
					</v-list-item>
				</v-list>
	
				<v-btn block color="primary" text @click="adicionarLink">
					<v-icon left>mdi-plus</v-icon>
					Adicionar Link
				</v-btn>
			</template>
		</GenericForm>

		<v-dialog v-model="mostraDialogoDeLink" max-width="500px">
			<v-form @submit.prevent="salvarLink" v-if="mostraDialogoDeLink">
				<v-card>
					<v-card-title>{{ indiceLinkEdicao === -1 ? 'Adicionar' : 'Editar' }} Link</v-card-title>
					<v-card-text>
						<v-text-field v-model="linkEditado.texto" label="Texto do Link" :rules="[obrigatorio]" />
						<v-text-field v-model="linkEditado.url" label="URL" :rules="[obrigatorio, urlValida]" />
					</v-card-text>
					<v-card-actions>
						<v-spacer />
						<v-btn text @click="fecharDialogLink">Cancelar</v-btn>
						<v-btn text color="primary" type="submit">Salvar</v-btn>
					</v-card-actions>
				</v-card>
			</v-form>
		</v-dialog>
	</Fragment>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import GenericForm from '@/components/GenericForm.vue'
import EventoService from '@/services/EventoService'
import { IEvento, IInputEvento as InputType, ITipoDaSessao } from '../../../common/types/Evento'
import { authModule, userModule } from '../../store'
import { obrigatorio, urlValida } from '../../shareds/regras'
import Irmao from '../../../common/types/Irmao'
import { tiposDeSessao, temPermissaoDeSessao, grausDeSessao } from '../../../common/shareds/sharedEvento'
import { displayEntidade } from '../../shareds/sharedsEvento'
import { useQueryClient } from '@tanstack/vue-query'
import { Loja } from 'common/types/Loja'
import { onBeforeRouteLeave, useRoute, useRouter } from '@/composables/router'

defineProps({
	novo: { type: Boolean, default: false },
	id: { type: String },
})

const queryClient = useQueryClient()
const router = useRouter()
const route = useRoute()

const genericForm = ref<{ data: InputType, validarSaida: () => false | void }>()

const service = EventoService

const mostraDialogoDeLink = ref(false)
const indiceLinkEdicao = ref(-1)
const linkEditado = ref({ texto: '', url: '' })

const lojas = computed(() => {
	if (!userModule.currentUser || userModule.currentUser.role === 'Visitante') return []
	return [
		userModule.currentUser.loja,
		...userModule.currentUser.lojas.map(loja => loja.loja),
	]
})

const opcoes = computed((): ITipoDaSessao[] => {
	if (
		!authModule.role ||
		!userModule.currentUser ||
		userModule.currentUser.role === 'Visitante'
	) return []

	return tiposDeSessao.filter(tipo => temPermissaoDeSessao(tipo, authModule.role)
		&& (tipo.tipoDaEntidade !== 'potencia' || (userModule.currentUser as Irmao).potencia.nome === tipo.nome))
})

function salvarEvento(evento: IEvento) {
	queryClient.invalidateQueries({
		queryKey: ['eventos'],
	})
	router.push({
		name: 'agenda',
		params: { id: evento._id },
	})
}

function criarObjeto(): InputType {
	const tipoDaSessao = opcoes.value[0]
	const evento: InputType = {
		tipoDaSessao,
		data: (route.query.data as string | undefined) || new Date().toISOString().split('T')[0],
		grauDaSessao: null,
		entidade: null,
		presencas: [],
		links: [],
	}
	alterarTipoDaSessao(evento, tipoDaSessao)
	return evento
}

function alterarTipoDaSessao(evento: InputType, tipoDaDessao: ITipoDaSessao) {
	if (!tipoDaDessao) {
		evento.entidade = null
		return
	}
	if (!userModule.currentUser || userModule.currentUser.role === 'Visitante') return

	evento.entidade = obterEntidadeDoUsuario(
		tipoDaDessao.tipoDaEntidade,
		userModule.currentUser,
	)
}

function alterarLojaDoEvento(evento: InputType, loja: Loja) {
	evento.entidade = loja
	evento.horario = evento.horario || loja.horarioDaSessao
}

function obterEntidadeDoUsuario(tipo: 'somar' | 'potencia' | 'loja', usuario: Irmao) {
	switch (tipo) {
		case 'somar':
			return usuario.somar ?? null
		case 'potencia':
			return usuario.potencia ?? null
		case 'loja':
			return usuario.loja ?? null
		default:
			return null
	}
}

function adicionarLink() {
	indiceLinkEdicao.value = -1
	linkEditado.value = { texto: '', url: '' }
	mostraDialogoDeLink.value = true
}

function editarLink(index: number) {
	indiceLinkEdicao.value = index
	linkEditado.value = { ...genericForm.value!.data.links[index] }
	mostraDialogoDeLink.value = true
}

function fecharDialogLink() {
	mostraDialogoDeLink.value = false
}

function salvarLink() {
	if (indiceLinkEdicao.value === -1) {
		genericForm.value!.data.links.push({ ...linkEditado.value })
	} else {
		genericForm.value!.data.links.splice(indiceLinkEdicao.value, 1, linkEditado.value)
	}
	fecharDialogLink()
}

function removerLink(index: number) {
	if (!confirm('Deseja remover este link?')) return
	genericForm.value!.data.links?.splice(index, 1)
}

onBeforeRouteLeave((to, from, next) => {
	next(genericForm.value!.validarSaida())
})
</script>
